import $ from 'jquery';

import { Skin } from 'lib/vendor/terrific/Application';

import { hashDeepLink } from 'lib/util/url';

import LegoContainer, {
	ServiceId,
	toFloat,
} from '../Tc.Module.LegoContainer';
// tslint:disable-next-line: no-duplicate-imports
import type {
	LegoContainerConfig,
	CurrentSelection,
	Flavour,
	Service,
	TrackingInfo,
	ConfigState,
} from '../Tc.Module.LegoContainer';

import type { SliderData } from './Configurator';

interface ConfiguredFlavour extends Flavour {
	price : number;
}

type CustomConfig = Record<ServiceId, Service<ConfiguredFlavour>>;

interface Block {
	siebelId : string;
	disableButtonOne : boolean;
	disableButtonTwo : boolean;
	blacklist : string;
}

interface ConfiguratorEntSkin extends LegoContainer {
	allSlidesInitialized : boolean;
	maxPrice : number;
	blocklist : Block[];
	customConfig? : CustomConfig;
	trackUserInteractionWithSlider : boolean;

	handleSliderInteraction(event : JQueryMouseEventObject) : void;
	handleButtonAction(event : JQueryMouseEventObject) : void;
	calculateMaxCost() : number;
	getHighestPrice(data : ConfiguredFlavour[]) : number;
	calculateSingleTotalPrice(data : ConfigState[] | CurrentSelection) : number;
	parseCustomConfig(data : {
		serviceId : ServiceId;
		flavours : ConfiguredFlavour[];
	}[]) : CustomConfig;
	findBlocklist(siebelId : string | undefined) : Block | null;
	checkBlockList() : void;
	getFullBlockList() : {
		list : string[];
		disableButtonOne : boolean;
		disableButtonTwo : boolean;
	}
	getActiveBundleLink() : JQuery | null;
	getUrlParameters() : string;
	setInitialState() : void;
	updatePriceBar(price : number, maxPrice : number) : void;
	updateServiceList() : void;
	updateSummaryBundleName() : void;
	onSliderSlideInitialized(data : SliderData) : void;
	onSliderSlideChanged(data : SliderData) : void;
}

/**
 * The Enterprise Lego Convigurator allows for a custom data model.
 * The data model is provided through the dialog as a json format,
 * that is then used to drive the summary bar.
 *
 * An additional feature is a dependancy model, that allows a selected
 * flavor to exclude flavours in other services from being selected.
 *
 * @author Matthias Inauen
 * @namespace Tc.Module.LegoContainer
 * @class LegoContainer
 * @decorator ConfiguratorEnt
 * @extends Tc.Module
 *
 */
// tslint:disable-next-line: variable-name
export const ConfiguratorEnt : Skin<LegoContainer, ConfiguratorEntSkin> = function ConfiguratorEnt(parent) {
	/**
	 * Initialize the component.
	 */
	this.initComponent = function() {
		parent.initComponent.call(this);
		this.sandbox.subscribe('SLIDER', this);

		this.allSlidesInitialized = false;
		this.maxPrice = 0;
		this.blocklist = this.$ctx.data('blacklist');
	};

	/**
	 * Attache event listeners for this instance
	 */
	this.addEventListeners = function() {
		parent.addEventListeners.call(this);
		this.ui.bundleLinks.on('click', this.handleBundleLink.bind(this));
		this.ui.slider.on('mousedown touchstart', this.handleSliderInteraction.bind(this));
		// Only the primary button receives the url parameters when clicked
		this.ui.summaryBar.find('.flat-button').on('click', this.handleButtonAction.bind(this));
	};

	/**
	 * Cache the ui elements needed in this configurator
	 */
	this.cacheUi = function() {
		parent.cacheUi.call(this);
		this.ui.slider = this.$ctx.find('.mod.modSlider');
		this.ui.slides = this.$ctx.find('.modSlide');
		this.ui.price = this.ui.summaryBar.find('.price');
		this.ui.priceBar = this.ui.price.find('.price-bar');
		this.ui.priceBarIndicator = this.ui.priceBar.find('.bar');
		this.ui.summaryBundleName = this.ui.summaryBar.find('.bundle-name');
		this.ui.buttonOne = this.ui.summaryBar.find('.modFlatButton').eq(0).find('a.flat-button');
		this.ui.buttonTwo = this.ui.summaryBar.find('.modFlatButton').eq(1).find('a.flat-button');
		this.ui.options = this.$ctx.find('[type="checkbox"]');
	};

	/**
	 * Calculate the max cost of the highest flavours
	 * @returns {number}
	 */
	this.calculateMaxCost = function() {
		let maxCost = 0;

		$.each((this.customConfig ?? {}), (_i, service : Service<ConfiguredFlavour>) => {
			maxCost += this.getHighestPrice(service.flavours);
		});

		return maxCost;
	};

	/**
	 * calculate the total price
	 */
	this.calculateSingleTotalPrice = function(data) {
		let totalPrice = 0;

		// Set currentSelection with calculated flavour prices
		$.each(data, (_i, item : LegoContainerConfig) => {
			this.currentSelection[item.sliderId] = {
				cost: this.getFlavourPrice(item.sliderId, item.slideId),
				slideId: item.slideId,
				sliderId: item.sliderId,
			};
		});

		// Now calculate the total price.
		$.each(this.currentSelection, (_i, item) => {
			totalPrice += item?.cost ?? 0;
		});

		return totalPrice;
	};

	/**
	 * Check the blacklist if any of the flavours need to be disabled
	 */
	this.checkBlockList = function() {
		const blocked = this.getFullBlockList();

		this.ui.slides.each((_i, slide) => {
			const slideId = $(slide).data('id');
			this.fire('setSlideState', {
				disable: (blocked.list.indexOf(slideId) > -1),
				slideId,
			}, ['SLIDER']); // Tc.Module.Slider.RotatingTeaser
		});

		this.ui.buttonOne.toggleClass('disabled', blocked.disableButtonOne);
		this.ui.buttonTwo.toggleClass('disabled', blocked.disableButtonTwo);
	};

	/**
	 * Get a full list of all blocked slides/flavours by all currently selected slides/flavours
	 * @returns {{blacklist: Array, disableButtonOne: boolean, disableButtonTwo: boolean}}
	 */
	this.getFullBlockList = function() {
		let blocklist : string[] = [];
		let disableButtonOne = false;
		let disableButtonTwo = false;

		$.each(this.currentSelection, (_i, item) => {
			const itemBlackList = this.findBlocklist(item?.slideId);

			if(itemBlackList) {
				blocklist = blocklist.concat(itemBlackList.blacklist.split(','));

				if(!disableButtonOne) {
					disableButtonOne = itemBlackList.disableButtonOne;
				}
				if(!disableButtonTwo) {
					disableButtonTwo = itemBlackList.disableButtonTwo;
				}
			}
		});

		return {list: blocklist, disableButtonOne, disableButtonTwo};
	};

	/**
	 * Find a specific blocklist in the blocklist array.
	 *
	 * @param siebelId - String
	 * @returns {*}
	 */
	this.findBlocklist = function(siebelId) {
		for(const block of this.blocklist) {
			if(block.siebelId === siebelId) {
				return block;
			}
		}

		return null;
	};
	/**
	 * Set the initial UI state.
	 */
	this.initUi = $.noop;

	/**
	 * Find the currently active bundle link
	 * @returns {*} - jQuery object with bundle link.
	 */
	this.getActiveBundleLink = function() {
		let $bundleLink : JQuery | null = null;

		this.ui.bundleLinks.each((_i, link) => {
			const card = $(link).find('.mod.modCard');
			if(card.hasClass('active')) {
				$bundleLink = $(link);

				return false; // Break jquery each loop
			}

			return undefined;
		});

		return $bundleLink;
	};

	/**
	 * Override the getFlavourPrice method, and adjust to this data model
	 */
	this.getFlavourPrice = function(serviceId, flavourId) {
		if(!this.customConfig) {
			return 0;
		}

		const service = this.customConfig[serviceId];

		if (!service) {
			return 0;
		}

		for(const flavour of service.flavours) {
			if (flavour.siebelId && flavour.siebelId === flavourId) {
				return toFloat(flavour.price);
			}
		}

		return 0;
	};

	/**
	 * Iterate over a flavours array and determine the flavour with the
	 * highest price. return that price.
	 *
	 * @param flavours - Array of flavours
	 * @returns {number} - the highest flavour price
	 */
	this.getHighestPrice = function(flavours) {
		let highestPrice = 0;

		for(const flavour of flavours) {
			highestPrice = Math.max(highestPrice, flavour.price);
		}

		return highestPrice;
	};

	/**
	 * Print the current selections as a parameter string
	 */
	this.getUrlParameters = function() {
		const parameters : string[] = [];

		$.each(this.currentSelection, function(key, item) {
			parameters.push(`${key}=${item?.slideId}`);
		});

		$.each(this.ui.options, (_i, checkbox) => {
			const $checkbox = $(checkbox);

			if($checkbox.prop('checked')) {
				parameters.push(String($checkbox.val()) + '=yes');
			}
		});

		return parameters.join('&');
	};

	/**
	 * Handle the primary button click event.
	 * Add the url parameters based on the current selection.
	 */
	this.handleButtonAction = function(event) {
		const parameters = this.getUrlParameters();
		const $link = $(event.currentTarget);
		const originAndSearch = $link.prop('href').split('?', 2);
		const origin = originAndSearch[0];
		let search = originAndSearch[1] || '';
		let hash = '';

		if(!origin) {
			return;
		}

		if(search && search.indexOf('#') > -1) {
			const searchAndHash = search.split('#', 2);

			search = searchAndHash[0];
			hash = '#' + searchAndHash[1];
		}

		search = '?' + [search, parameters].filter(Boolean).join('&');
		const newUrl = origin + search + hash;

		$link.prop('href', newUrl);
	};

	/**
	 * Event handler. Handling the users interaction with a slide.
	 */
	this.handleSliderInteraction = function() {
		this.trackUserInteractionWithSlider = true;
	};

	/**
	 * Event handler. Collapses/Expands the summary bar on mobile devices
	 */
	this.handleToggleCollapse = function(event) {
		const $icon = this.ui.collapseButton.find('.icon');

		this.ui.price.toggleClass('hidden', !$icon.is('.icon-016-chevron-up'));
		parent.handleToggleCollapse.call(this, event);
	};


	/**
	 * Override the loadData method, but instead of actually loading
	 * data, parse the passed in data into a data object the base
	 * class can understand. Then start the instance by calling handleDataSuccess.
	 */
	this.loadData = function() {
		// If the config object is already set, don't load again.
		if (!$.isEmptyObject(this.customConfig)) {
			return;
		}

		this.customConfig = this.parseCustomConfig(this.$ctx.data('custom-config'));
		// Prevent errors in LegoContainer#track, which relies on config info even though the types differ
		// FIXME: Refactor…
		this.config = this.customConfig as any;

		this.log.debug('loadData', this.customConfig);

		this.maxPrice = this.calculateMaxCost();
	};

	/**
	 * Parsing the custom config into the proper form, so the base class automatically
	 * understands the data format.
	 * @param data
	 */
	this.parseCustomConfig = function(data) {
		const config = {} as any as CustomConfig;

		for(const item of data) {
			config[item.serviceId] = {
				flavours: item.flavours,
			};
		}

		return config;
	};

	/**
	 * Set the initial state of this configurator
	 */
	this.setInitialState = function() {
		const deepLinks = this.deserializeDeeplink(hashDeepLink.get('lpIds'));

		if(deepLinks && deepLinks.config.length) {
			this.preselectSliderPositions(deepLinks.config);
			// This.trackInitialConfiguration(deepLinks.config);
			if(!Object.keys(deepLinks.options).length) {
				this.setInitialOptions(this.options);
			} else {
				this.setInitialOptions(deepLinks.options);
			}
			this.updateSummaryUi(deepLinks.config);
		} else {
			const updateData = this.getUpdateDataFromCurrentSelection();
			this.setInitialOptions(this.options);
			this.updateSummaryUi(updateData);
			// This.trackInitialConfiguration(updateData);
		}
	};

	/**
	 * Setting the price bar indicator width to the precentage of the currentPrice/maxPrice
	 * @param price	- Number - the current price
	 * @param maxPrice - Number - the maximum price that can be reached
	 */
	this.updatePriceBar = function(price, maxPrice) {
		const newWidth = this.ui.priceBar.width()! * price / maxPrice;
		this.ui.priceBarIndicator.css('width', newWidth);
	};

	/**
	 * Update the service list items in the summary bar
	 */
	this.updateServiceList = function() {
		this.ui.services.addClass('hidden');
		$.each(this.currentSelection, (_i, item) => {
			this.ui.services.filter(`[data-depends-on="${item!.slideId}"]`).removeClass('hidden');
		});
	};

	/**
	 * Update the bundle name in the summary bar.
	 * Receive a list of class names and find a class name that contains 'bundleName-'
	 */
	this.updateSummaryBundleName = function() {
		const bundleLink = this.getActiveBundleLink();

		if(!bundleLink) {
			this.ui.summaryBundleName.text(' ');

			return;
		}

		const classList = bundleLink.prop('classList');
		// tslint:disable-next-line: prefer-for-of
		for(let i = 0; i < classList.length; i += 1) {
			if(classList[i].indexOf('bundleName-') > -1) {
				this.ui.summaryBundleName.text(classList[i].replace('bundleName-', ''));

				return;
			}
		}

		this.ui.summaryBundleName.text(' ');
	};

	/**
	 * Update the ui of the summary bar.
	 *
	 * @param data
	 */
	this.updateSummaryUi = function(data) {
		const totalPrice = this.calculateSingleTotalPrice(data);

		this.updatePriceBar(totalPrice, this.maxPrice);
		this.isBundleConfiguration();
		this.updateSummaryBundleName();
		this.updateServiceList();
		this.checkBlockList();
	};


	/**
	 * Terrific Handler, fired by Tc.Module.Slider
	 * @param {Object} data
	 */
	this.onSliderSlideInitialized = function(data) {
		this.slidersInitialized += 1;
		this.allSlidesInitialized = this.slidersInitialized === this.ui.slider.length;
		this.onSliderSlideChanged(data);

		// Wait until all sliders on the page have finished initializing.
		if(this.allSlidesInitialized) {
			this.setInitialState();
		}
	};

	/**
	 * Terrific Handler, fired by Tc.Module.Slider
	 * @param {Object} data
	 */
	this.onSliderSlideChanged = function(data) {
		const mod = this;
		mod.updateSummaryUi([data as any as LegoContainerConfig]);

		if(mod.trackUserInteractionWithSlider) {
			mod.trackUserInteractionWithSlider = false;

			const trackingInfo : TrackingInfo = {
				action: 'select',
				category: 'optionchange',
				label: data.slideId,
				location: mod.getLocationString(),
				selectedProducts: [{slideId: data.slideId} as any as LegoContainerConfig],
			};

			mod.track(trackingInfo);
		}
	};
};



// WEBPACK FOOTER //
// typescript://../clientlibs/js/lib/ecommerce/lego/skins/ConfiguratorEnt.ts